import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Check, Users, Info, Search } from 'lucide-react';
import * as skillsService from '../../../../utils/onboardingV2/skillsService.ts';
import styles from './ProfileConnectSelector.module.scss';

interface SkillCategory {
  id: number;
  name: string;
}

interface ProfileConnectSelectorProps {
  selectedCategories: number[];
  onChange: (categories: number[]) => void;
  compact?: boolean;
}

const INITIAL_VISIBLE_COUNT = 8;

const ProfileConnectSelector: React.FC<ProfileConnectSelectorProps> = ({
  selectedCategories,
  onChange,
  compact = false,
}) => {
  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await skillsService.fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCategories();
  }, []);

  const handleCategoryClick = (categoryId: number) => {
    const isSelected = selectedCategories.includes(categoryId);
    const updated = isSelected
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];
    onChange(updated);
  };

  const displayCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const base = q
      ? categories.filter((c) => c.name.toLowerCase().includes(q))
      : categories.slice(0, INITIAL_VISIBLE_COUNT);

    const selectedExtras = selectedCategories
      .filter((id) => !base.some((c) => c.id === id))
      .map((id) => categories.find((c) => c.id === id))
      .filter((c): c is SkillCategory => c != null);

    const seen = new Set<number>();
    const out: SkillCategory[] = [];
    for (const c of [...selectedExtras, ...base]) {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        out.push(c);
      }
    }
    return out;
  }, [categories, searchQuery, selectedCategories]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando categorías...</div>
      </div>
    );
  }

  const showSearchHint =
    !searchQuery.trim() && categories.length > INITIAL_VISIBLE_COUNT;

  return (
    <div className={styles.container}>
      {!compact && (
        <>
          <div className={styles.header}>
            <Users size={16} />
            <span>¿Con qué perfiles querés conectar?</span>
          </div>

          <div className={styles.infoMessage}>
            <Info size={16} />
            <span>
              Seleccioná las categorías de profesionales o empresas con las que te gustaría conectar en
              Edify.
            </span>
          </div>
        </>
      )}

      {selectedCategories.length > 0 && (
        <div className={styles.selectedSummary}>
          <span className={styles.selectedSummary_label}>Seleccionados:</span>
          <span className={styles.selectedSummary_count}>{selectedCategories.length} categoría{selectedCategories.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      <div className={styles.searchRow}>
        <Search className={styles.searchIcon} size={20} aria-hidden />
        <input
          type="search"
          className={styles.searchInput}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar categoría (ej: obra, comercio)"
          autoComplete="off"
          enterKeyHint="search"
        />
      </div>
      {showSearchHint ? (
        <p className={styles.searchHint}>
          Mostramos algunas opciones. Buscá para ver el listado completo.
        </p>
      ) : null}

      <div className={styles.categoriesGrid}>
        {displayCategories.length === 0 ? (
          <p className={styles.emptyResults}>No se encontraron categorías con ese criterio.</p>
        ) : (
          displayCategories.map((category) => {
            const isSelected = selectedCategories.includes(category.id);
            return (
              <motion.button
                key={category.id}
                type="button"
                className={`${styles.categoryPill} ${isSelected ? styles.active : ''}`}
                onClick={() => handleCategoryClick(category.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSelected && <Check size={16} className={styles.checkIcon} />}
                {category.name}
              </motion.button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ProfileConnectSelector;
